import React, { FC } from 'react';
import { Spin } from 'antd';
import type useCalendar from '../../../assessment-portal/hooks/use-calendar';
import { WEEKDAY_LABELS } from '../../data/constants';
import type { MonthDirection } from '../../utils/booking-analytics';
import MonthNav from './month-nav';

interface DesktopCalendarProps {
  calendar: ReturnType<typeof useCalendar>
  availableDates: string[]
  loading: boolean
  onChangeMonth: (direction: MonthDirection) => void
  onSelectDay: (day: number | null) => void
}

function getCalendarCellClassName(isSelected: boolean, isAvailable: boolean, isPastDay: boolean, day: number | null): string {
  if (isSelected) return 'bg-brand text-white font-bold cursor-pointer';
  if (isAvailable) return 'bg-brand bg-opacity-5 text-brand font-bold cursor-pointer';
  if (isPastDay || !day) return 'text-slate-300 cursor-default';
  return 'text-slate-400 cursor-default';
}

const DesktopCalendar: FC<DesktopCalendarProps> = ({
  calendar,
  availableDates,
  loading,
  onChangeMonth,
  onSelectDay,
}) => (
  <Spin spinning={loading}>
    <div className="border-2 border-slate-200 rounded-xl overflow-hidden">
      <MonthNav
        calendarMonth={calendar.calendarMonth}
        calendarYear={calendar.calendarYear}
        onChangeMonth={onChangeMonth}
        className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-brand bg-opacity-5"
        buttonClassName="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-200 text-slate-600 font-bold"
      />
      <div className="grid grid-cols-7 border-b border-slate-100">
        {WEEKDAY_LABELS.map((d) => <div key={d} className="text-center text-xs font-bold text-slate-400 py-2">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 p-2 gap-1">
        {calendar.calendarDays.map((cell) => {
          const { key: cellKey, day: d } = cell;
          const avail = calendar.isAvailableDay(d, availableDates);
          const isSelected = calendar.isSelectedDay(d);
          const past = calendar.isPast(d);

          return (
            <button
              key={cellKey}
              type="button"
              onClick={() => onSelectDay(d)}
              disabled={!avail}
              className={`aspect-square flex items-center justify-center rounded-lg text-sm transition-all ${getCalendarCellClassName(isSelected, avail, past, d)}`}
            >
              {d || ''}
            </button>
          );
        })}
      </div>
      <div className="px-4 pb-3 flex items-center gap-4 text-xs text-slate-400">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded inline-block bg-brand bg-opacity-10" />
          {' '}
          Available
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded inline-block bg-brand" />
          {' '}
          Selected
        </span>
      </div>
    </div>
  </Spin>
);

export default DesktopCalendar;
